/**
 * OracleIntelligencePanel — the Oracle Phase 3+4 "investigative analyst"
 * workspace. Sits directly under the ArchiveToolbar and above the
 * DossierGrid on the Archive page. Only renders when the user has
 * submitted a search (URL `?search=...`).
 *
 * Preserves every existing surface:
 *   • ArchiveToolbar autocomplete unchanged (uses /api/topics/suggest)
 *   • Grid regex-filter behaviour unchanged
 *   • ArchiveFilters / mobile responsive layout unchanged
 *
 * Additive UI (Phase 3):
 *   • "Oracle interpreted your search as: [chip] [chip] …"
 *   • Loading state: ORACLE ANALYZING…  (blinking terminal cursor)
 *   • Empty state: "No direct dossier found. Oracle searched: …"
 *   • Top-N results with hero, status badge, evidence + source counts,
 *     3 reasoning chips, and a subtle confidence meter (★ ★ ★ ★ ★).
 *
 * Investigation workspace (Phase 4):
 *   • Interpretation chips are clickable — click appends the chip to
 *     the query; click again removes it (highlighted while active).
 *   • Investigation Stack: parses the current query into tokens and
 *     shows them as removable chips with an arrow trail.
 *   • Investigation History: localStorage-backed session trail; every
 *     committed query is deduped and pushed. Click restores instantly.
 *
 * Data source: GET /api/oracle/search?q=...
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowRight, Search, ShieldCheck, FileText, X, History } from "lucide-react";
import { EnvironmentalAtmosphere } from "../EnvironmentalAtmosphere";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Editorial rule: at most 3 reasoning chips per result. Keeps the
// card compact so the "case file" aesthetic isn't drowned in
// annotations.
const MAX_REASONS_PER_RESULT = 3;
const MAX_RESULTS = 6;

// Confidence tiers derived from the raw score returned by Oracle.
// Tuning: exact title = 100; strong multi-signal = 60+; single tag
// or category = ~10. Never displays "0%" — anything below 5 is
// hidden per the "hide gracefully" rule.
const _confidence = (score) => {
  if (!score || score < 5) return null;
  if (score >= 60) return { stars: 5, label: "Strong Match" };
  if (score >= 35) return { stars: 4, label: "Confident Match" };
  if (score >= 20) return { stars: 3, label: "Probable Match" };
  if (score >= 10) return { stars: 2, label: "Possible Match" };
  return { stars: 1, label: "Weak Signal" };
};

const _statusColor = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "restricted") return "#3F3B36";
  if (s === "closed") return "#D92D20";
  if (s === "archived") return "#B7B0A4";
  if (s === "under_review" || s === "review" || s === "monitored") return "#E0A526";
  return "#3FB950";  // active / published / ongoing / default
};
const _statusLabel = (status) => {
  const s = (status || "").toLowerCase();
  const map = {
    active: "ACTIVE", published: "ACTIVE", ongoing: "ACTIVE",
    under_review: "UNDER REVIEW", review: "UNDER REVIEW",
    monitored: "UNDER REVIEW", archived: "ARCHIVED",
    closed: "CLOSED", restricted: "RESTRICTED", draft: "DRAFT",
  };
  return map[s] || "ACTIVE";
};

// Terminal-style typewriter animation via CSS keyframe on a hidden
// pseudo-caret. Kept in a tag so the whole component is self-contained.
const _typewriterStyle = `
@keyframes _oracle_blink { 0%,49% { opacity: 1 } 50%,100% { opacity: 0 } }
.oracle-caret::after {
  content: "▊"; color: #D92D20; margin-left: 6px;
  animation: _oracle_blink 900ms steps(1) infinite;
}
`;

// -----------------------------------------------------------------------
// Query token model — tokenize on whitespace but preserve quoted phrases
// as a single token (e.g. "Allen Dulles" stays one chip).
// -----------------------------------------------------------------------
const _tokenize = (q) => {
  if (!q) return [];
  const out = [];
  const re = /"([^"]+)"|(\S+)/g;
  let m;
  while ((m = re.exec(q)) !== null) {
    out.push(m[1] || m[2]);
  }
  return out;
};
const _quoteIfNeeded = (tok) => (tok && tok.includes(" ") ? `"${tok}"` : tok);
const _detokenize = (tokens) => tokens.map(_quoteIfNeeded).join(" ");
const _tokEq = (a, b) => (a || "").toLowerCase() === (b || "").toLowerCase();

// Investigation-history persistence — session-scoped so a browser
// refresh keeps the trail, but closing the tab clears it.
const _HISTORY_KEY = "archive_oracle_investigation_history_v1";
const _HISTORY_MAX = 8;
const _historyGet = () => {
  try {
    const raw = sessionStorage.getItem(_HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
};
const _historyPush = (query) => {
  const q = (query || "").trim();
  if (!q) return;
  try {
    const prev = _historyGet().filter((h) => h.toLowerCase() !== q.toLowerCase());
    prev.unshift(q);
    sessionStorage.setItem(_HISTORY_KEY, JSON.stringify(prev.slice(0, _HISTORY_MAX)));
  } catch { /* quota exceeded — ignore */ }
};

export const OracleIntelligencePanel = ({ query }) => {
  const [state, setState] = useState({ loading: false, data: null, error: null });
  const [, setParams] = useSearchParams();
  const [history, setHistory] = useState(_historyGet);

  // Active tokens parsed from the URL query — the source of truth for
  // the Investigation Stack and for chip "active" highlighting.
  const activeTokens = useMemo(() => _tokenize(query || ""), [query]);

  // Chip-click handler: toggle a token in/out of the URL query.
  // Preserves ArchiveToolbar's URL-sync contract (`onParamChange`
  // in Archive.jsx also writes to setParams).
  const _toggleToken = useCallback((token) => {
    const t = String(token || "").trim();
    if (!t) return;
    const has = activeTokens.some((x) => _tokEq(x, t));
    const next = has
      ? activeTokens.filter((x) => !_tokEq(x, t))
      : [...activeTokens, t];
    const nextQuery = _detokenize(next);
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      if (nextQuery) p.set("search", nextQuery);
      else p.delete("search");
      return p;
    }, { replace: true });
  }, [activeTokens, setParams]);

  const _removeToken = useCallback((token) => _toggleToken(token), [_toggleToken]);

  const _restoreHistory = useCallback((q) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("search", q);
      return p;
    });
  }, [setParams]);

  useEffect(() => {
    if (!query || query.length < 2) {
      setState({ loading: false, data: null, error: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetch(`${API}/oracle/search?q=${encodeURIComponent(query)}&limit=${MAX_RESULTS}`, {
      // AbortController would be cleaner, but this closure-guard keeps
      // parity with the rest of the codebase's fetch patterns.
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (!cancelled) {
          setState({ loading: false, data, error: null });
          _historyPush(query);
          setHistory(_historyGet());
        }
      })
      .catch((err) => { if (!cancelled) setState({ loading: false, data: null, error: String(err) }); });
    return () => { cancelled = true; };
  }, [query]);

  if (!query || query.length < 2) return null;

  const { loading, data } = state;
  const results = (data && data.results) || [];
  const entities = (data && data.matched_entities) || [];
  const aliases = (data && data.matched_aliases) || [];
  const years = (data && data.matched_years) || [];
  const hasInterpretation = entities.length + aliases.length + years.length > 0;
  const showStack = activeTokens.length >= 1;

  return (
    <section
      data-testid="oracle-intelligence-panel"
      className="relative overflow-hidden border-b"
      style={{ background: "#0B0A0D", borderColor: "#2D2A26" }}
    >
      {/* Locked branding — Oracle runs the atmosphere in "operations"
          variant at 0.6 intensity (cooler surveillance-room mood). */}
      <EnvironmentalAtmosphere variant="operations" intensity={0.6} />
      <style>{_typewriterStyle}</style>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        {/* Section marker — Oracle branding */}
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1.5 h-1.5 rounded-full bg-[#D92D20] animate-pulse" />
          <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#D92D20]">
            Oracle Intelligence
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#7B7168]">
            · &quot;{query}&quot;
          </span>
        </div>

        {/* Investigation Stack — active tokens as removable chips.
            Renders before the interpretation row so the visitor's own
            query is the first thing they see. */}
        {showStack && (
          <div data-testid="oracle-stack" className="mb-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#D92D20] mb-2">
              Current Investigation
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {activeTokens.map((tok, i) => (
                <React.Fragment key={`stk-${i}-${tok}`}>
                  {i > 0 && (
                    <span className="text-[#454038] font-mono text-[11px]" aria-hidden="true">
                      →
                    </span>
                  )}
                  <button
                    type="button"
                    data-testid={`oracle-stack-token-${i}`}
                    onClick={() => _removeToken(tok)}
                    aria-label={`Remove ${tok} from investigation`}
                    className="group inline-flex items-center gap-1.5 px-2.5 py-1 border font-mono text-[10px] uppercase tracking-[0.22em] text-[#E4DFD3] hover:border-[#D92D20] hover:text-[#D92D20] transition-colors"
                    style={{ borderColor: "#D92D20", background: "#180E0E" }}
                  >
                    <span>{tok}</span>
                    <X size={9} className="text-[#7B7168] group-hover:text-[#D92D20]" />
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Loading — "ORACLE ANALYZING…" with blinking caret */}
        {loading && (
          <div data-testid="oracle-loading"
               className="font-mono text-[13px] uppercase tracking-[0.35em] text-[#E4DFD3] oracle-caret">
            Oracle analyzing
          </div>
        )}

        {/* Interpretation row — matched entities / aliases / years */}
        {!loading && hasInterpretation && (
          <div data-testid="oracle-interpretation" className="mb-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#7B7168] mb-2">
              Oracle interpreted your search as — <span className="text-[#B7B0A4]">click to refine</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {entities.slice(0, 6).map((e) => {
                const isActive = activeTokens.some((t) => _tokEq(t, e.name));
                return (
                  <button
                    key={`ent-${e.slug}`}
                    type="button"
                    data-testid="oracle-interp-entity"
                    onClick={() => _toggleToken(e.name)}
                    aria-pressed={isActive}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 border font-mono text-[10px] uppercase tracking-[0.22em] transition-colors"
                    style={{
                      borderColor: isActive ? "#D92D20" : "#3A3630",
                      background: isActive ? "#180E0E" : "#0F0E12",
                      color: isActive ? "#D92D20" : "#E4DFD3",
                    }}
                    title={e.kind ? `Entity type: ${e.kind}` : undefined}
                  >
                    <ShieldCheck size={10} className="text-[#D92D20]" />
                    {e.name}
                  </button>
                );
              })}
              {aliases.slice(0, 4).map((a) => {
                const isActive = activeTokens.some((t) => _tokEq(t, a));
                return (
                  <button
                    key={`ali-${a}`}
                    type="button"
                    data-testid="oracle-interp-alias"
                    onClick={() => _toggleToken(a)}
                    aria-pressed={isActive}
                    className="inline-block px-2.5 py-1 border font-mono text-[10px] uppercase tracking-[0.22em] transition-colors"
                    style={{
                      borderColor: isActive ? "#D92D20" : "#2D2A26",
                      background: isActive ? "#180E0E" : "transparent",
                      color: isActive ? "#D92D20" : "#B7B0A4",
                    }}
                  >
                    {a}
                  </button>
                );
              })}
              {years.map((y) => {
                const isActive = activeTokens.some((t) => _tokEq(t, String(y)));
                return (
                  <button
                    key={`yr-${y}`}
                    type="button"
                    data-testid="oracle-interp-year"
                    onClick={() => _toggleToken(String(y))}
                    aria-pressed={isActive}
                    className="inline-block px-2.5 py-1 border font-mono text-[10px] tracking-[0.15em] transition-colors"
                    style={{
                      borderColor: isActive ? "#D92D20" : "#3A3630",
                      background: isActive ? "#180E0E" : "transparent",
                      color: isActive ? "#D92D20" : "#E0A526",
                    }}
                  >
                    {y}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty results — investigative-guide messaging */}
        {!loading && data && results.length === 0 && (
          <div data-testid="oracle-empty" className="py-4">
            <div className="font-heading text-[16px] text-[#E4DFD3]">
              No direct dossier found.
            </div>
            <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.24em] text-[#7B7168]">
              Oracle searched:
            </div>
            <ul className="mt-1 space-y-0.5 font-mono text-[12px] text-[#B7B0A4]">
              <li>• Titles</li>
              <li>• People</li>
              <li>• Organizations</li>
              <li>• Operations</li>
              <li>• Timeline</li>
              <li>• Aliases &amp; acronyms</li>
            </ul>
            <div className="mt-4 font-mono text-[11px] text-[#7B7168]">
              Try another investigative angle — a person, an organization, or a year.
            </div>
          </div>
        )}

        {/* Results */}
        {!loading && results.length > 0 && (
          <ol
            data-testid="oracle-results"
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            {results.map((r, i) => {
              const conf = _confidence(r.score);
              return (
                <li key={r.slug} data-testid={`oracle-result-${r.slug}`}>
                  <Link
                    to={`/topic/${r.slug}`}
                    className="group flex gap-4 border p-4 hover:border-[#D92D20] transition-colors"
                    style={{ borderColor: "#2D2A26", background: "#0F0E12" }}
                  >
                    {/* Hero thumbnail — only rendered when a real image
                        exists on disk (backend hydrates image_url). */}
                    {r.image_url && (
                      <div
                        className="w-16 h-16 md:w-20 md:h-20 shrink-0 bg-cover bg-center border"
                        style={{ backgroundImage: `url(${r.image_url})`, borderColor: "#2D2A26" }}
                        aria-hidden="true"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      {/* Status badge + rank */}
                      <div className="flex items-center gap-2 mb-1.5">
                        <span
                          className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.28em]"
                          style={{ color: _statusColor(r.status) }}
                          data-testid={`oracle-result-status-${r.slug}`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: _statusColor(r.status) }} />
                          {_statusLabel(r.status)}
                        </span>
                        <span className="font-mono text-[9px] tracking-[0.28em] text-[#454038]">
                          · #{i + 1}
                        </span>
                      </div>
                      <div className="text-[15px] font-heading text-[#E4DFD3] leading-tight group-hover:text-[#D92D20] transition-colors">
                        {r.title}
                      </div>
                      {r.subtitle && (
                        <div className="mt-1 font-mono text-[11px] text-[#A39E93] line-clamp-2">
                          {r.subtitle}
                        </div>
                      )}
                      {/* Metadata row — only real counts, hidden if zero */}
                      <div className="mt-2 flex flex-wrap items-center gap-3 font-mono text-[9px] uppercase tracking-[0.24em] text-[#7B7168]">
                        {r.evidence_count > 0 && (
                          <span className="flex items-center gap-1">
                            <FileText size={9} className="text-[#D92D20]" />
                            {r.evidence_count} <span className="text-[#454038]">evidence</span>
                          </span>
                        )}
                        {r.source_count > 0 && (
                          <span className="flex items-center gap-1">
                            {r.source_count} <span className="text-[#454038]">sources</span>
                          </span>
                        )}
                      </div>
                      {/* Reasoning chips — this is the "Why this result?"
                          transparency layer. Capped at 3 for card breath. */}
                      {Array.isArray(r.reasons) && r.reasons.length > 0 && (
                        <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid={`oracle-result-reasons-${r.slug}`}>
                          {r.reasons.slice(0, MAX_REASONS_PER_RESULT).map((reason, j) => (
                            <span
                              key={j}
                              className="inline-block font-mono text-[9px] uppercase tracking-[0.18em] text-[#B7B0A4] border-l pl-1.5"
                              style={{ borderColor: "#3A3630" }}
                            >
                              {reason}
                            </span>
                          ))}
                        </div>
                      )}
                      {/* Confidence — subtle, right-side reinforcement */}
                      {conf && (
                        <div
                          className="mt-2.5 flex items-center justify-between gap-2"
                          data-testid={`oracle-result-confidence-${r.slug}`}
                        >
                          <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-[#7B7168]">
                            {conf.label}
                          </span>
                          <span className="text-[#D92D20] tracking-[0.15em] text-[11px]" aria-hidden="true">
                            {"★".repeat(conf.stars)}
                            <span className="text-[#3A3630]">{"★".repeat(5 - conf.stars)}</span>
                          </span>
                        </div>
                      )}
                    </div>
                    <ArrowRight
                      size={14}
                      className="text-[#454038] group-hover:text-[#D92D20] shrink-0 mt-1 transition-colors"
                    />
                  </Link>
                </li>
              );
            })}
          </ol>
        )}

        {/* Investigation History — session-scoped trail. Renders when
            we have at least one past query DIFFERENT from the current
            one; single-entry sessions don't need a "history" callout. */}
        {history.filter((h) => !_tokEq(h, query)).length > 0 && (
          <div
            data-testid="oracle-history"
            className="mt-6 pt-4 border-t"
            style={{ borderColor: "#2D2A26" }}
          >
            <div className="flex items-center gap-2 mb-2">
              <History size={11} className="text-[#7B7168]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#7B7168]">
                Recent Investigation Trail
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {history
                .filter((h) => !_tokEq(h, query))
                .map((h, i) => (
                  <button
                    key={`hist-${i}-${h}`}
                    type="button"
                    data-testid={`oracle-history-item-${i}`}
                    onClick={() => _restoreHistory(h)}
                    className="inline-block px-2.5 py-1 border font-mono text-[10px] uppercase tracking-[0.20em] text-[#B7B0A4] hover:text-[#E4DFD3] hover:border-[#7B7168] transition-colors"
                    style={{ borderColor: "#2D2A26" }}
                  >
                    {h.length > 42 ? h.slice(0, 42) + "…" : h}
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default OracleIntelligencePanel;
